import useTranslation from "next-translate/useTranslation"

import { ImplementationState, IProject, ProjectProgress, ProjectState } from "@api/schema"
import StatusIndicator from "@components/common/form/StatusIndicator"
import { IconName } from "@components/common/Icon"

interface IProps {
  project: IProject
  simple?: boolean
}

/**
 * This component provides a coloured status of an project for the cards in the marketplace
 */
const ProjectStatusIndicator: React.FC<IProps> = ({ project, simple = false }: IProps) => {
  const { t } = useTranslation("common")

  /**
   * calculates data to present the project status accordingly
   *
   * @returns className, label and icon, depending on the status of the project
   */
  const projectProgressData = (p: IProject): { className: string; label: string; icon?: IconName } => {
    if (p.state === ProjectState.Inactive) {
      return {
        className: "text-gray-light",
        label: "project.state.inactive"
      }
    }

    // define const to use it twice
    const ideaPresentation = {
      className: "",
      label: "project.progressSimple.idea",
      icon: "light-bulb" as IconName
    }
    if (!p.implementationState) {
      // if no implementationState is given it must be an idea
      // because ideas do not have an implementationState
      return ideaPresentation
    }

    // implementationState is prior to project.progress, which is used if implementationState===Planning
    switch (p.implementationState) {
      case (ImplementationState.Planning): {
        switch (p.progress) {
          case (
            ProjectProgress.ProposalSubmitted ||
            ProjectProgress.CreatingProposal ||
            ProjectProgress.SubmittingProposal
          ):
            return {
              className: "",
              label: "project.progressSimple.applicationPhase",
              icon: "project"
            }
          case (ProjectProgress.CreatingPlan):
            return {
              className: "",
              label: "project.progressSimple.projectPlan",
              icon: "project"
            }
          case (ProjectProgress.Idea):
            return ideaPresentation
          default:
            return {
              className: "",
              label: "project.progressSimple.profilePhase",
              icon: "project"
            }
        }
      }
      case (ImplementationState.Running):
        return {
          className: "",
          label: "project.progressSimple.running",
          icon: "project-implementation"
        }
      case (ImplementationState.Finished):
        return {
          className: "",
          label: "project.progressSimple.finished",
          icon: "check-in-circle"
        }
    }
  }


  const { className, label, icon } = projectProgressData(project)

  return <StatusIndicator
    className={className}
    icon={icon}
  >
    {t(simple ? label : `project.progress.${project.progress}`)}
  </StatusIndicator>
}

export default ProjectStatusIndicator
